import { useEffect, useRef, useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { createVersionMonitor, isBuildVersion, readReloadRecord, RELOAD_RECORD_KEY, VERSION_CHECK_INTERVAL } from '../lib/version-monitor.js';

// Forms retain their dirty state even after an unsuccessful async submit.
// Closing/unmounting or resetting the form releases the automatic update.
export function trackFormEdits(doc: Document) {
  const dirty = new Set<Element>();
  const edit = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const owner = target.closest('form, [contenteditable="true"]');
    if (owner) dirty.add(owner);
  };
  const reset = (event: Event) => { if (event.target instanceof Element) dirty.delete(event.target); };
  doc.addEventListener('input', edit, true);
  doc.addEventListener('change', edit, true);
  doc.addEventListener('reset', reset, true);
  return {
    isEditing() {
      for (const element of dirty) {
        // Native dialogs remain mounted when closed; they no longer hold an
        // active draft (the suggestion dialog also clears its fields on close).
        if (!element.isConnected || element.closest('dialog:not([open])')) dirty.delete(element);
      }
      const focused = doc.activeElement;
      return dirty.size > 0 || Boolean(doc.querySelector('[data-update-dirty="true"]'))
        || Boolean(focused?.matches('input, textarea, select, [contenteditable="true"]'));
    },
    dispose() {
      doc.removeEventListener('input', edit, true);
      doc.removeEventListener('change', edit, true);
      doc.removeEventListener('reset', reset, true);
    },
  };
}

export function AppUpdates() {
  const [pending, setPending] = useState(false);
  const mutationCount = useIsMutating();
  const saving = useRef(false);
  saving.current = mutationCount > 0;
  const update = useRef<(() => void) | null>(null);
  useEffect(() => {
    // Vite already applies development changes through HMR. Only production
    // HTML carries this marker, in both the local server and Vercel.
    const currentVersion = document.querySelector('meta[name="vitrine-build"]')?.getAttribute('content');
    if (!isBuildVersion(currentVersion)) return;
    const forms = trackFormEdits(document);
    let lastInteraction = 0;
    const interact = () => { lastInteraction = Date.now(); };
    const monitor = createVersionMonitor({
      currentVersion,
      fetchVersion: async () => {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 8_000);
        try {
          const response = await fetch('/__app-version', {
            cache: 'no-store', credentials: 'same-origin', redirect: 'error',
            headers: { Accept: 'application/json' }, signal: controller.signal,
          });
          if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) return null;
          const body: unknown = await response.json();
          return body && typeof body === 'object' && 'version' in body ? body.version : null;
        } finally { window.clearTimeout(timeout); }
      },
      isActive: () => document.visibilityState === 'visible' && navigator.onLine,
      canReload: () => !saving.current && !forms.isEditing() && Date.now() - lastInteraction > 3_000,
      reload: () => window.location.reload(),
      onPending: setPending,
      readRecord: () => { try { return readReloadRecord(sessionStorage.getItem(RELOAD_RECORD_KEY)); } catch { return null; } },
      writeRecord: (record) => {
        try { sessionStorage.setItem(RELOAD_RECORD_KEY, JSON.stringify(record)); return true; }
        catch { return false; }
      },
    });
    const check = () => { void monitor.check(); };
    update.current = () => { void monitor.check(true); };
    const interval = window.setInterval(check, VERSION_CHECK_INTERVAL);
    window.addEventListener('pageshow', check);
    window.addEventListener('online', check);
    document.addEventListener('visibilitychange', check);
    document.addEventListener('pointerdown', interact, true);
    document.addEventListener('keydown', interact, true);
    check();
    return () => {
      monitor.stop(); forms.dispose(); update.current = null;
      window.clearInterval(interval);
      window.removeEventListener('pageshow', check);
      window.removeEventListener('online', check);
      document.removeEventListener('visibilitychange', check);
      document.removeEventListener('pointerdown', interact, true);
      document.removeEventListener('keydown', interact, true);
    };
  }, []);
  if (!pending) return null;
  return <aside className="app-update-notice" role="status" aria-live="polite">
    <div><strong>Uma melhoria está disponível</strong><p>Se estiver editando, salve suas alterações antes de atualizar.</p></div>
    <button type="button" onClick={() => update.current?.()}>Atualizar agora</button>
  </aside>;
}
